import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/QuestionView.css';
import mixpanel from '../../utils/mixpanel'
import { AlertTriangle } from 'lucide-react';
import { useSessionCreation } from '../../contexts/SessionCreationContext';
import QuestionLayout, { QUESTION_MODES } from '../../features/questions/Question/QuestionLayout';
import QuestionDisplay, { QUESTION_TYPES } from '../../features/questions/Question/components/QuestionDisplay';
//import DiagnosticComplete from '../../features/questions/Question/components/DiagnosticComplete';
import ResultsReview from '../../features/questions/Question/components/ResultsReview';
import HiraganaLoader from '../../components/HiraganaLoader';

import { API_URL } from '../../app/constants'

const DIAGNOSTIC_QUESTIONS_API_URL = `${API_URL}/api/v1/panel/diagnostic/get-questions`;
const REPORT_ISSUE_API_URL = `${API_URL}/api/v1/panel/report-issue`;

function DiagnosticExam() {
  const SUBMIT_API_URL = `${API_URL}/api/v1/panel/diagnostic/submit`;
  const navigate = useNavigate();
  const { exam_id } = useParams();
  const { triggerSessionCreation } = useSessionCreation();

  // State management
  const [responses, setResponses] = useState([]);
  const [content, setContent] = useState([]);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timerMinutes, setTimerMinutes] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState('');
  const [otherIssueText, setOtherIssueText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [viewState] = useState('question'); // 'question', 'complete', 'results'
  const [examResults, setExamResults] = useState(null);
  const [showMotivationalMessage, setShowMotivationalMessage] = useState(false);
  const [currentMotivationalMessage, setCurrentMotivationalMessage] = useState('');
  
  // Analytics state for diagnostic exam
  const [analytics, setAnalytics] = useState(null);

  // Motivational messages for diagnostic exam
  const motivationalMessages = {
    2: "Adjusting your training plan...",
    4: "Analyzing your strengths...",
    6: "Almost there! Keep going...",
    // Dynamic messages based on remaining questions
    3: "Just 3 more questions!",
    1: "Final push - you're doing great!"
  };

  const examIdNumber = Number(exam_id);
  const hasFetchedQuestions = useRef(false);
  
  // Fetch analytics when exam is completed
  useEffect(() => {
    if (viewState === 'complete') {
      const fetchAnalytics = async () => {
        try {
          const jwt = localStorage.getItem('jwt');
          const response = await fetch(`${API_URL}/api/v1/panel/reports/diagnostic/analytics`, {
            headers: { 'Authorization': `Bearer ${jwt}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            setAnalytics(data);
            console.log("Fetched analytics:", data);
          }
        } catch (error) {
          console.error("Failed to fetch analytics:", error);
        }
      };
      
      fetchAnalytics();
    }
  }, [viewState]);

  // Format time for display
  const formatTime = useCallback(() => {
    return `${String(timerMinutes).padStart(2, '0')}:${String(timerSeconds).padStart(2, '0')}`;
  }, [timerMinutes, timerSeconds]);
  
  // Get total time in seconds
  const getTotalTimeInSeconds = useCallback(() => {
    return timerMinutes * 60 + timerSeconds;
  }, [timerMinutes, timerSeconds]);

  // Fetch questions from API
  useEffect(() => {
    const fetchQuestions = async () => {
      const startTime = Date.now();
      try {
        setLoading(true);
        const jwt = localStorage.getItem('jwt');
        const response = await fetch(`${DIAGNOSTIC_QUESTIONS_API_URL}/${examIdNumber}`, {
          method: "GET",
          headers: {
            'Authorization': `Bearer ${jwt}`
          },
        });
        const data = await response.json();
        console.log("Diagnostic questions:", data);
        console.log("Raw API response for diagnostic:", data);
        setContent(data.map(question => {
          console.log("Processing question:", question);
          return { 
            ...question, 
            selectedAnswer: null, 
            isAnswered: false 
          };
        }));
        
        // Track exam start
        mixpanel.track('Exam Started', {
          exam_id: examIdNumber,
          exam_type: 'diagnostic_exam',
          total_questions: data.length,
          start_time: new Date().toISOString()
        });
        
        // Ensure minimum 2 seconds loading time
        const elapsedTime = Date.now() - startTime;
        const minLoadingTime = 2000; // 2 seconds
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      } catch (error) {
        console.error("Error fetching questions:", error);
        setError("Error fetching questions");
        
        // Even on error, ensure minimum loading time
        const elapsedTime = Date.now() - startTime;
        const minLoadingTime = 2000;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          setLoading(false);
        }, remainingTime);
      }
    };

    if (!hasFetchedQuestions.current) {
      fetchQuestions();
      hasFetchedQuestions.current = true;
    }
  }, [examIdNumber]);

  // Set question start time for tracking duration
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentContentIndex]);

  // Ref to store timer interval ID
  const timerIntervalRef = useRef(null);
  
  // Timer effect
  useEffect(() => {
    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    // Start new timer only if in question view and content is loaded
    if (viewState === 'question' && !loading && content.length > 0) {
      console.log("Starting timer");
      timerIntervalRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          if (prev === 59) {
            setTimerMinutes(min => min + 1);
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      console.log("Timer not started, viewState:", viewState);
    }

    // Cleanup function
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        console.log("Timer cleared");
      }
    };
  }, [viewState, loading, content.length]);
  
  // Format text to handle newlines and spacing
  const formatText = useCallback((text) => {
    if (!text || text === '0') return '';
    return text.replace(/\\n/g, '\n');
  }, []);

  // Handle answer selection
  const handleAnswerSelection = (index) => {
    const timeTaken = Math.round((Date.now() - questionStartTime) / 1000);

    // Update content array with the selected answer
    const updatedContent = content.map((question, idx) =>
      idx === currentContentIndex ? { ...question, selectedAnswer: index } : question
    );
    setContent(updatedContent);

    // Update responses array with the new answer
    const questionId = content[currentContentIndex].question_id;
    const selectedAnswer = content[currentContentIndex].choices.choices[index];

    const newResponses = responses.filter(response => response.question_id !== questionId);
    newResponses.push({
      question_id: questionId,
      selected_answer: selectedAnswer,
      time_taken: timeTaken
    });
    setResponses(newResponses);
    
    // Track question answered
    const currentQuestion = content[currentContentIndex];
    const isCorrect = selectedAnswer === currentQuestion.correct_answer;
    
    mixpanel.track('Question Answered', {
      exam_id: examIdNumber,
      exam_type: 'diagnostic_exam',
      question_id: questionId,
      question_number: currentContentIndex + 1,
      user_answer: selectedAnswer,
      correct_answer: currentQuestion.correct_answer,
      is_correct: isCorrect,
      time_spent_seconds: timeTaken,
      question_section: currentQuestion.section || 'unknown'
    });
  };

  // Show motivational message
  const showMotivationalPopup = (message) => {
    setCurrentMotivationalMessage(message);
    setShowMotivationalMessage(true);
    // Auto-hide the message after 3 seconds
    setTimeout(() => {
      setShowMotivationalMessage(false);
    }, 3000);
  };

  // Navigate to next question
  const handleNextQuestion = () => {
    if (currentContentIndex < content.length - 1) {
      const nextQuestionNumber = currentContentIndex + 2; // +2 because currentContentIndex is 0-based
      const remainingQuestions = content.length - (currentContentIndex + 1);
      
      setCurrentContentIndex(currentContentIndex + 1);
      setQuestionStartTime(Date.now());
      
      // Check for motivational messages based on question number or remaining questions
      let messageToShow = null;
      
      // Check for remaining questions messages first (higher priority)
      if (remainingQuestions === 3) {
        messageToShow = motivationalMessages[3]; // "Just 3 more questions!"
      } else if (remainingQuestions === 1) {
        messageToShow = motivationalMessages[1]; // "Final push - you're doing great!"
      }
      // Check for question number based messages (only if no remaining message)
      else if (nextQuestionNumber === 3 || nextQuestionNumber === 5 || nextQuestionNumber === 7) {
        if (nextQuestionNumber === 3) {
          messageToShow = motivationalMessages[2]; // "Adjusting your training plan..."
        } else if (nextQuestionNumber === 5) {
          messageToShow = motivationalMessages[4]; // "Analyzing your strengths..."
        } else if (nextQuestionNumber === 7) {
          messageToShow = motivationalMessages[6]; // "Almost there! Keep going..."
        }
      }
      
      // Show the message if we have one
      if (messageToShow) {
        // Small delay to let the question transition happen first
        setTimeout(() => {
          showMotivationalPopup(messageToShow);
        }, 500);
      }
    } else {
      handleSubmit();
    }
  };

  // Go back to previous question
  const handlePreviousQuestion = () => {
    if (currentContentIndex > 0) {
      setCurrentContentIndex(currentContentIndex - 1);
    }
  };

  // Handle exit button click
  const handleExit = (e) => {
    e?.preventDefault();
    setShowExitModal(true);
  };

  // Show session complete screen
  // const showCompleteScreen = (responseData) => {
  //   // Calculate correct answers for display in complete screen
  //   const correctAnswers = responses.reduce((count, response) => {
  //     const question = content.find(q => q.question_id === response.question_id);
  //     if (!question) return count;
      
  //     const correctAnswer = question.correct_answer || "";
  //     return response.selected_answer === correctAnswer ? count + 1 : count;
  //   }, 0);
    
  //   setExamResults({
  //     totalQuestions: content.length,
  //     totalAnswered: responses.length,
  //     correctAnswers: correctAnswers,
  //     score: Math.round((correctAnswers / content.length) * 100),
  //     timeSpent: getTotalTimeInSeconds(),
  //     data: responseData
  //   });
  //   setViewState('complete');
  // };

  // Handle view results button click
  const handleViewResults = () => {
    // Navigate to diagnostic-stats
    navigate('/diagnostic-stats');
  };

  // Format result data for the results review screen
  const prepareResultsData = () => {
    console.log("Preparing results data with responses:", responses);
    
    return content.map(question => {
      const response = responses.find(r => r.question_id === question.question_id);
      console.log(`Question ${question.question_id} response:`, response);
      
      // If no response, mark as incorrect
      if (!response) {
        console.log(`No response found for question ${question.question_id}`);
        return {
          id: question.question_id,
          question: question.question,
          instruction: question.instruction,
          isCorrect: false,
          selectedAnswer: null,
          selectedAnswerIndex: null,
          isBookmarked: false
        };
      }
      
      const selectedAnswerIndex = question.choices.choices.findIndex(c => c === response.selected_answer);
      console.log(`Selected answer index for question ${question.question_id}:`, selectedAnswerIndex);
      
      // Get correct answer index for comparison
      const correctAnswerText = question.correct_answer || "";
      const correctAnswerIndex = question.choices.choices.findIndex(c => c === correctAnswerText);
      console.log(`Correct answer index for question ${question.question_id}:`, correctAnswerIndex, "Correct answer:", correctAnswerText);
      
      // Check if the selected answer matches the correct answer
      const isCorrect = response.selected_answer === correctAnswerText;
      console.log(`Is correct for question ${question.question_id}:`, isCorrect);
      
      return {
        id: question.question_id,
        question: question.question,
        instruction: question.instruction,
        isCorrect: isCorrect,
        selectedAnswer: response.selected_answer,
        selectedAnswerIndex: selectedAnswerIndex >= 0 ? selectedAnswerIndex : null,
        isBookmarked: false
      };
    });
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    navigate('/review-personalized/diagnostic'); // Navigate to review-personalized/diagnostic instead of dashboard
  };


  // Submit exam answers
  const handleSubmit = async () => {
    if (currentContentIndex === content.length - 1 && !isSubmitting) {
      setIsSubmitting(true);
      
      // Explicitly stop the timer when submitting
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        console.log("Timer stopped at submission");
      }
      
      const requestBody = { diagnostic_exam_id: exam_id, responses: responses };
      console.log("Submitting diagnostic exam:", requestBody);
      
      try {
        const jwt = localStorage.getItem('jwt');
        const response = await fetch(SUBMIT_API_URL, {
          method: "POST",
          headers: { 
            'Authorization': `Bearer ${jwt}`, 
            "Content-Type": "application/json" 
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to submit exam: ${response.status} ${errorText}`);
        }

        const responseData = await response.json();
        console.log("Diagnostic exam submitted successfully:", responseData);
        
        // Start background session creation
        triggerSessionCreation();
        
        // Navigate directly to diagnostic stats (analysis will be sent by email)
        navigate('/diagnostic-stats');

        // Calculate detailed analytics
        const totalTimeMinutes = timerMinutes + (timerSeconds / 60);
        const correctAnswers = responses.filter(r => {
          const question = content.find(q => q.question_id === r.question_id);
          return question && r.selected_answer === question.correct_answer;
        }).length;
        const scorePercentage = Math.round((correctAnswers / content.length) * 100);
        const averageTimePerQuestion = Math.round((totalTimeMinutes * 60) / content.length);
        
        // Track detailed exam completion
        mixpanel.track('Exam Completed', {
          exam_id: exam_id,
          exam_type: "diagnostic_exam",
          total_questions: content.length,
          correct_answers: correctAnswers,
          score_percentage: scorePercentage,
          total_time_minutes: Math.round(totalTimeMinutes * 100) / 100,
          total_time_seconds: (timerMinutes * 60) + timerSeconds,
          average_time_per_question_seconds: averageTimePerQuestion,
          completion_time: new Date().toISOString()
        });

        // Fetch the correct answers for each question if not already available
        // This step ensures we have all correct answers available
        let updatedContent = [...content];
        
        // Process content to include correct answers where missing
        updatedContent = updatedContent.map(question => {
          // If correct_answer exists and is not empty, use it
          if (question.correct_answer && question.correct_answer !== "") {
            return question;
          }
          
          // Try to determine correct answer from choices if possible
          // This is a fallback if correct_answer is not set
          try {
            // Assumption: correct answer might be marked in choices
            // You may need to adapt this logic based on your data structure
            return {
              ...question,
              correct_answer: question.choices.choices[0] // Use first choice as fallback
            };
          } catch (err) {
            console.warn(`Could not determine correct answer for question ${question.question_id}`, err);
            return question;
          }
        });
        
        // Replace content with updated version
        setContent(updatedContent);
        
        // Calculate correct answers with the updated content
        const finalCorrectAnswers = responses.reduce((count, response) => {
          const question = updatedContent.find(q => q.question_id === response.question_id);
          if (!question) return count;
          
          const correctAnswer = question.correct_answer || "";
          return response.selected_answer === correctAnswer ? count + 1 : count;
        }, 0);
        
        const totalQuestions = updatedContent.length;
        const score = Math.round((finalCorrectAnswers / totalQuestions) * 100);
        
        console.log('Calculated score:', {
          correctAnswers: finalCorrectAnswers,
          totalQuestions,
          score,
          responses: responses.length
        });
        
        // Set exam results with score - do this before changing view state
        setExamResults({
          totalQuestions: totalQuestions,
          totalAnswered: responses.length,
          correctAnswers: finalCorrectAnswers,
          score: score,
          timeSpent: getTotalTimeInSeconds()
        });
        
        // No more waiting - we've already navigated
        // Background session creation will happen on the backend
        
      } catch (error) {
        console.error("Failed to submit exam:", error.message);
        alert(`Failed to submit exam: ${error.message}`);
        setIsSubmitting(false);
      }
    }
  };

  // Open report issue modal
  const handleReportIssue = () => {
    setShowReportModal(true);
  };

  // Submit issue report
  const handleSubmitIssue = async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const issueMessage = selectedIssue === "Other" ? otherIssueText : selectedIssue;
      
      const response = await fetch(REPORT_ISSUE_API_URL, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${jwt}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          question_id: content[currentContentIndex]?.question_id,
          issue_message: issueMessage,
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to submit issue: ${response.status} ${errorText}`);
      }

      alert("Issue reported successfully!");
      setShowReportModal(false);
      setSelectedIssue('');
      setOtherIssueText('');
    } catch (error) {
      console.error("Failed to submit issue:", error.message);
    }
  };

  // Loading state
  if (loading) {
    return <HiraganaLoader />;
  }


  // Error state
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  
  // No questions state
  if (content.length === 0 || !content[currentContentIndex]) {
    return <div className="error-message">No questions available.</div>;
  }
  
  // Render diagnostic complete screen
  if (viewState === 'complete') {
    // Just use the examResults and analytics data we already have
    console.log("Analytics data:", analytics);
    console.log("Exam results:", examResults);
    
    // Get user data if available
    const userDataStr = localStorage.getItem('userData');
    let userData = {};
    try {
      userData = userDataStr ? JSON.parse(userDataStr) : {};
    } catch (e) {
      console.error("Error parsing user data:", e);
    }
    
    // Get first name or username from email
    let userName = userData.first_name || "";
    if (!userName && userData.email) {
      userName = userData.email.split('@')[0];
      // Capitalize the first letter of username from email
      userName = userName.charAt(0).toUpperCase() + userName.slice(1);
    }
    // If still no name, use a generic placeholder
    if (!userName) {
      userName = "Your";
    }
    
    return (
      <div className="diagnostic-complete-screen">
        <div className="diagnostic-complete-content">
          <div className="header-section">
            <div className="test-result-section">
              <p className="test-result-label">Test result</p>
              <h1 className="test-result-title">{userName}'s level test results report</h1>
            </div>
            <button 
              className="view-results-button" 
              onClick={handleViewResults}
            >
              View results
            </button>
          </div>
          
          <div className="description-section">
            <p className="description-text">
              Mina has analyzed your JLPT skills based on our diagnostic test.
            </p>
          </div>
          
          <div className="results-section">
            <div className="score-section">
              <h2 className="section-title">Score: {examResults?.score || 0}%</h2>
              <div className="progress-bar">
                <div className="progress-value" style={{width: `${examResults?.score || 0}%`}}></div>
              </div>
              <p className="score-description">
                You answered {examResults?.correctAnswers || 0} out of {examResults?.totalQuestions || 0} questions correctly.
              </p>
            </div>
            
            <div className="recommendation-section">
              <h2 className="section-title">Your recommended study</h2>
              <p className="recommendation-text">
                Based on your results, we recommend focusing on basic grammar and vocabulary concepts.
              </p>
              <p className="start-text">
                Mina is preparing your personalized study plan based on your diagnostic results.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render results review screen
  if (viewState === 'results') {
    return (
      <ResultsReview
        questions={prepareResultsData()}
        performanceData={{
          streak: 0,
          timeSpent: getTotalTimeInSeconds()
        }}
        onBackToDashboard={handleBackToDashboard}
      />
    );
  }

  // Get current question
  const currentQuestion = content[currentContentIndex];
  const { script, question, instruction, choices } = currentQuestion;
  const hasScript = script && script !== "0" && script !== 0;
  const hasAnswer = currentQuestion.selectedAnswer !== null;
  
  // Prepare options for the QuestionDisplay component
  const options = [
    ...choices.choices.map((choice, index) => ({ id: index, text: choice }))
  ];

  return (
    <>
      <QuestionLayout
        timer={formatTime()}
        totalQuestions={content.length}
        currentQuestion={currentContentIndex + 1}
        onExit={handleExit}
        isAnswered={false} // Diagnostic exams don't show correct/incorrect immediately
        mode={QUESTION_MODES.SOLVING}
        footer={
          <>
            <button 
              className="report-btn" 
              onClick={handleReportIssue}
            >
              <AlertTriangle size={20} />
              <span className="report-text">Report Issue</span>
            </button>
            
            <div className="navigation-buttons">
              <button 
                className="nav-btn previous" 
                disabled={currentContentIndex === 0}
                onClick={handlePreviousQuestion}
              >
                Previous
              </button>
              
              <button 
                className="nav-btn next" 
                onClick={handleNextQuestion}
                disabled={!hasAnswer || isSubmitting}
              >
                {currentContentIndex === content.length - 1 ? 
                  (isSubmitting ? "Submitting..." : "Finish") : 
                  "Next"}
              </button>
            </div>
          </>
        }
      >
        <QuestionDisplay
          type={QUESTION_TYPES.SINGLE}
          title={`Question ${currentContentIndex + 1}`}
          instruction={instruction}
          script={hasScript ? script : null}
          questionText={question}
          options={options}
          selectedAnswer={currentQuestion.selectedAnswer}
          isAnswered={false}
          onAnswerSelect={handleAnswerSelection}
          formatText={formatText}
          imageUrl={(() => {
            // Map diagnostic exam IDs to levels
            const examIdToLevel = {
              110: "N5",
              111: "N4", 
              112: "N3",
              113: "N2",
              114: "N1"
            };
            
            const examLevel = examIdToLevel[examIdNumber] || "N5";
            
            // Define which question numbers should have images for each exam
            const questionWithImages = {
              114: [16], // N1: only question 16
              113: [15], // N2: only question 15
              112: [14, 15], // N3: questions 14 and 15
              111: [14, 15], // N4: questions 14 and 15
              110: [12] // N5: only question 12
            };
            
            const allowedQuestions = questionWithImages[examIdNumber] || [];
            const shouldShowImage = allowedQuestions.includes(currentContentIndex + 1);
            
            if (!shouldShowImage) {
              return null;
            }
            
            return currentQuestion?.script_image?.urls?.[0] ||
                   currentQuestion?.script_image_url ||
                   (currentQuestion.related && Array.isArray(currentQuestion.related) && currentQuestion.related.length > 0 ?
                     `/images/${examLevel}/reading/script/${currentQuestion.related[0]}.png` :
                     (currentQuestion.ref_id ?
                       `/images/${examLevel}/reading/script/${currentQuestion.ref_id}.png` :
                       null));
          })()}
        />
      </QuestionLayout>

      {/* Exit Modal */}
      {showExitModal && (
        <div className="listening-master-modal-overlay">
          <div className="listening-master-modal-content">
            <h3>Exit</h3>
            <p>Are you sure you want to exit?<br/>Your progress will be lost.</p>
            <div className="listening-master-modal-buttons">
              <button 
                className="listening-master-modal-cancel"
                onClick={() => setShowExitModal(false)}
              >
                Cancel
              </button>
              <button 
                className="listening-master-modal-confirm"
                onClick={() => {
                  mixpanel.track('Cancel Diagnostic Exam', {
                    exam_id: exam_id,
                    question_id: currentQuestion?.question_id,
                    question_number: currentContentIndex + 1,
                  });
                  navigate('/');
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportModal && (
        <div className="modal-overlay">
          <div className="report-modal">
            <h3>
              Report Issue
              <button onClick={() => setShowReportModal(false)}>×</button>
            </h3>
            <h4>What's wrong?</h4>
            <div className="report-options">
              {[
                { value: 'None of the options is correct', label: 'None of the options is correct' },
                { value: 'Question has incorrect or missing data', label: 'Question has incorrect or missing data' },
                { value: 'Questions/Options/Infographics are not visible properly or missing', label: 'Questions/Options/Infographics are not visible properly or missing' },
                { value: 'Question or option has a typo', label: 'Question or option has a typo' },
                { value: 'The question is confusing or unclear', label: 'The question is confusing or unclear' },
                { value: 'Other', label: 'Other' }
              ].map(option => (
                <label key={option.value} className="report-option">
                  <input 
                    type="radio" 
                    name="issue" 
                    value={option.value}
                    checked={selectedIssue === option.value}
                    onChange={(e) => setSelectedIssue(e.target.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            
            {selectedIssue === 'Other' && (
              <div className="other-description">
                <label>Description of issue:</label>
                <textarea
                  value={otherIssueText}
                  onChange={(e) => setOtherIssueText(e.target.value)}
                  placeholder="Please describe the issue..."
                  rows={4}
                />
              </div>
            )}
            
            <button 
              className="submit-issue" 
              onClick={handleSubmitIssue}
              disabled={selectedIssue === 'Other' && !otherIssueText.trim()}
            >
              Submit Issue
            </button>
          </div>
        </div>
      )}

      {/* Motivational Message Notification */}
      {showMotivationalMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#9333EA',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
          zIndex: 1200,
          fontSize: '14px',
          fontWeight: '500',
          animation: 'slideInDown 0.3s ease-out',
          backdropFilter: 'blur(4px)'
        }}>
          {currentMotivationalMessage}
        </div>
      )}
    </>
  );
}

export default DiagnosticExam;